import InfoIcon from "@components/boxes/InfoIcon";
import LabelRow from "@components/rowcomponents/LabelRow";
import { TransactionByIdResponseData } from "@services/models/Transactions/TransactionByIdModel";
import React from "react";
import ConstantFunctions from "../../../utils/constants_functions";
import TransactionTypeSymbols from "./TransactionTypeSymbols";
import { getTransactionTypeText } from "./TransactionTypeText";

interface PrimePhpEurTransactionContainerProps {
  transactionData: TransactionByIdResponseData;
}

const PrimePhpEurTransactionContainer: React.FC<
  PrimePhpEurTransactionContainerProps
> = ({ transactionData }) => {
  const constantFunctions = new ConstantFunctions();

  const displayValueOrDash = (value: any) => (value ? value.toString() : "--");
  const isValuePresent = (value: any) => !!value;

  const isEurTransaction = transactionData.fiatCurrencySymbol === "EUR";

  var payinPayoutType = "";
  if (transactionData?.isBuying === 1) {
    payinPayoutType = "Payin";
  } else {
    payinPayoutType = "Payout";
  }

  return (
    <div className="border border-bg-header overflow-scroll flex flex-col rounded-lg mx-[20px]">
      <div className="border-b border-b-bg-header px-[16px] py-[18px] flex flex-row justify-between">
        <div className="flex flex-row">
          <TransactionTypeSymbols
            transactionType={transactionData?.transactionType ?? ""}
            ledger={transactionData?.ledger ?? ""}
          />
          <span className="text-[16px] font-medium text-text-base">
            {transactionData?.ledger === "CR" ? "Received" : "Sent"}
          </span>
        </div>
        <div className="flex flex-row items-center gap-[4px]">
          <span className="text-[16px] font-bold text-text-base">
            {displayValueOrDash(
              constantFunctions.formatAmount(transactionData?.amount),
            )}
          </span>
          <img
            src={transactionData?.cryptoCurrencyImage}
            width={18}
            height={18}
          />
        </div>
      </div>
      <div className="flex flex-col space-y-[16px] p-[16px]">
        <LabelRow
          label={"Transaction Type"}
          value={displayValueOrDash(
            getTransactionTypeText(
              transactionData?.transactionType ?? "",
              transactionData?.internalType ?? "",
            ),
          )}
        />
        <LabelRow
          label={"Date & Time"}
          value={`${constantFunctions.formatDateTime(
            transactionData?.updatedAt ?? transactionData?.createdAt ?? "",
          )}`}
        />
      </div>
      <div className="border-b border-b-bg-header"></div>
      <div className="flex flex-col space-y-[16px] p-[16px]">
        <div className="flex flex-row items-center gap-[4px]">
          <InfoIcon />
          <span className="text-[14px] font-normal text-text-base">
            Cross Ramp Transaction Details
          </span>
        </div>
        <LabelRow
          label={"Instance ID"}
          value={displayValueOrDash(transactionData?.instanceId)}
          isCopy={isValuePresent(transactionData?.instanceId)}
        />
        <LabelRow
          label={"MOID"}
          value={displayValueOrDash(transactionData?.merchantOrderId)}
          isCopy={isValuePresent(transactionData?.merchantOrderId)}
        />
        <LabelRow label={"Trade Type"} value={payinPayoutType} />
        <LabelRow
          label={"Token | Fiat"}
          value={`${displayValueOrDash(
            transactionData.cryptoCurrencySymbol,
          )} | ${displayValueOrDash(transactionData.fiatCurrencySymbol)}`}
        />
        <LabelRow
          label={"Amount In fiat"}
          value={`${displayValueOrDash(
            constantFunctions.formatAmount(transactionData.fiatAmount),
          )} ${displayValueOrDash(transactionData.fiatCurrencySymbol)}`}
        />
        <LabelRow
          label={"Amount In Crypto"}
          value={`${displayValueOrDash(
            constantFunctions.formatAmount(transactionData.amount),
          )} ${displayValueOrDash(transactionData.cryptoCurrencySymbol)}`}
        />
        <LabelRow
          label={"Conversion Rate"}
          valueComp={
            <div className="flex flex-row items-center">
              <p className="text-text-base text-[12px] font-medium mr-[4px]">
                1.00
              </p>
              {transactionData.fiatCurrencyImage && (
                <img
                  src={transactionData.fiatCurrencyImage}
                  height={16}
                  width={16}
                  className="mr-[4px]"
                />
              )}
              <p className="text-text-base text-[12px] font-medium mr-[4px]">
                =
              </p>
              <p className="text-text-base text-[12px] font-medium mr-[4px]">
                {displayValueOrDash(transactionData.conversionRate)}
              </p>
              {transactionData.cryptoCurrencyImage && (
                <img
                  src={transactionData.cryptoCurrencyImage}
                  height={16}
                  width={16}
                />
              )}
            </div>
          }
        />
        {isEurTransaction && (
          <>
            <LabelRow
              label={"MDR"}
              value={`${displayValueOrDash(transactionData.MDR)}%`}
            />
            <LabelRow
              label={"Fees"}
              value={`${displayValueOrDash(
                constantFunctions.formatAmount(transactionData.fees),
              )} ${displayValueOrDash(transactionData.cryptoCurrencySymbol)}`}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PrimePhpEurTransactionContainer;
